import { useMutation } from "@tanstack/react-query";
import fetchHandler from "../fetchHandler";
import { toast } from "react-toastify";
import { useAuth } from "../../providers/auth-context";
import { AuthenticationLoginResponse } from "../Authentication/interfaces";

interface TwoFactorAuthConfirmPayload {
  otp: string;
  google2fa_secret: string;
}

export interface TwoFactorAuthVerifyPayload {
  email: string;
  password: string;
  otp: string;
}

/**
 *
 * Initialize QR code and secret code generation
 * as a preparation for enabling 2FA
 *
 */
export function useTwoFactorAuthSetup() {
  return useMutation({
    mutationFn: async () => {
      return await fetchHandler("POST", "2fa/setup");
    },
    onError: error => error,
  });
}

/**
 *
 * Confirm the 2FA for the currently logged in account
 * using the generated secret and OTP
 *
 */
export function useTwoFactorAuthConfirm() {
  const { handleUpdateUserDetails } = useAuth();

  return useMutation({
    mutationFn: async (payload: TwoFactorAuthConfirmPayload) => {
      return await fetchHandler("POST", "2fa/confirm", payload);
    },
    onSuccess: () => {
      toast.success("Successfully enabled 2FA for your account!");

      // Update authentication context details
      handleUpdateUserDetails({ is_2fa_enabled: true });
    },
    onError: error => error,
  });
}

/**
 *
 * Remove 2FA functionality from the currently logged in account
 *
 */
export function useTwoFactorAuthDisable() {
  const { handleUpdateUserDetails } = useAuth();

  return useMutation({
    mutationFn: async () => {
      return await fetchHandler("POST", "2fa/disable");
    },
    onSuccess: () => {
      toast.success("Successfully disabled 2FA from your account!");

      // Update authentication context details
      handleUpdateUserDetails({ is_2fa_enabled: false });
    },
    onError: error => error,
  });
}

/**
 *
 *
 * Verify the provided 2FA - OTP code and if valid,
 * login the user into the application
 *
 */
export function useTwoFactorAuthVerify() {
  const { handleAuthenticationTokens } = useAuth();

  return useMutation({
    mutationFn: async (otp: TwoFactorAuthVerifyPayload) => {
      return (await fetchHandler("POST", "2fa/verify", otp)) as AuthenticationLoginResponse;
    },
    onSuccess: response => {
      // Throw error if there are no tokens available
      if (response.long_lived && !response.access_token) {
        throw new Error("No Access Token received from the API.");
      }

      if (!response.long_lived && (!response.access_token || !response.refresh_token)) {
        throw new Error("No token(s) received from the API.");
      }

      // Save authentication tokens in local storage and update authentication context
      handleAuthenticationTokens({
        access_token: response.access_token,
        refresh_token: !response.long_lived ? response.refresh_token : null,
        expires_in: response.expires_in,
        long_lived: response.long_lived,
        autologin: false,
      });
    },
    onError: error => error,
  });
}
